import axios from 'axios';
import PropTypes from 'prop-types';
import React from 'react';
import { PageContext } from '../../../contexts/ContextCreator';
import { isEmpty } from '../../../utilities/helperFunctions';
import { EmitMetric } from '../../Analytics/VisibilityPixel/VisibilityPixel';
import {
    eventStatusTypes,
    registrationSourceTypes,
    userEventStatusTypes,
} from '../../Includes/Constants/events';
import { eventPropTypesShape } from '../../Includes/eventsProps';
import { getEventStatus, isEventRegistrationClosed } from '../helperFunctions';

import {
    AttendingButton,
    EventOverButton,
    InProgressButton,
    RegisterButton,
    RegistrationClosedButton,
} from './EventRegisterButtons';

import { eventRegistrationFlowType } from '../../Modals/Flows/EventRegistration/EventRegistration';
import {
    QUICK_PROFILE_STEP_NAME,
    SELECT_TIME_STEP_NAME,
    EVENT_REQUIREMENTS_INPUT_STEP_NAME,
    CONFIRM_SUCCESS_STEP_NAME,
} from '../../Modals/Flows/Steps/constants';

import { formatPreferredTimeProps } from '../../Modals/Flows/EventRegistration/constants';

import './EventRegisterButtonContainer.scss';

const {
    registrationFlow,
    checkHasRequirements,
} = require('../../Modals/Flows/EventRegistration/constants');

const { registrationFlowAPI } = registrationFlow;

const registerButtonClickMetric = { login_trigger: 'register-event' };

export default class EventRegisterButtonContainer extends React.PureComponent {
    static propTypes = {
        /**
         * single event data
         */
        event: eventPropTypesShape.isRequired,
        /**
         * whether this register button is rendered from single event page or not
         */
        isSingleEventPage: PropTypes.bool.isRequired,
        /**
         * whether user is register for the event or not
         */
        isUserRegistered: PropTypes.bool.isRequired,
        /**
         * set userRegistered to true
         */
        setUserAsRegistered: PropTypes.func.isRequired,
        /**
         * current time of the application is being rendered
         */
        currentTime: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    };

    static defaultProps = {
        event: {},
        isSingleEventPage: false,
        isUserRegistered: false,
        currentTime: null,
        setUserAsRegistered: () => {},
    };

    static contextType = PageContext;

    triggerEventRegisterModal = async () => {
        const {
            toggleJobProfileSnippetComplete,
            triggerFlowModal,
            jobProfileSnippet,
            pageProps: { session },
        } = this.context;

        const { setUserAsRegistered, event } = this.props;
        const { vreProfile = {}, vreAdditionalInfo, hash, sourceType } = event;
        const { isCompleted } = jobProfileSnippet;

        const quickProfileRequired = !isCompleted;
        const eventReqInputStepRequired = checkHasRequirements(
            vreAdditionalInfo
        );

        const baseQuickProfile = session.name
            ? { name: session.name, education: {}, experience: {} }
            : {};

        const requiredSteps = {
            [QUICK_PROFILE_STEP_NAME]: quickProfileRequired,
            [EVENT_REQUIREMENTS_INPUT_STEP_NAME]: eventReqInputStepRequired,
        };

        // if we have quick profile fields or a selected time, pass them in
        // if they aren't included, they will be set to the step defaults defined
        // in Modals/Flows/Steps/constants
        const rawPopulateProps = {
            quickProfile: baseQuickProfile,
            eventRequirementsInput: vreProfile,
        };

        const modalData = {
            stepPopulateProps: registrationFlow.buildDefaultRegFlowState(
                rawPopulateProps
            ),
            generalModalProps: {
                event,
                setUserAsRegistered,
                requiredSteps,
                eventPreferredTimes: [],
                startStep: QUICK_PROFILE_STEP_NAME,
                toggleJobProfileSnippetComplete,
                sourceType,
            },
        };

        this.handleEventRegisterClickMetrics();

        if (quickProfileRequired) {
            return triggerFlowModal(eventRegistrationFlowType, modalData);
        }

        if (!quickProfileRequired && eventReqInputStepRequired) {
            modalData.generalModalProps.startStep = EVENT_REQUIREMENTS_INPUT_STEP_NAME;
            return triggerFlowModal(eventRegistrationFlowType, modalData);
        }

        // no quick profile needed, no event requirements -> register user and check for eventPreferredTimes
        // if we have them, set initial step to time select step otherwise confirmSuccess step
        if (!quickProfileRequired && !eventReqInputStepRequired) {
            const regPayload = {
                eventHash: hash,
                registerPayload: {},
            };

            const {
                ok,
                data,
                error,
            } = await registrationFlowAPI.submitEventRegistration(regPayload);

            if (!ok) {
                console.log('REG ERRORS: ', error);
                return console.log('REGISTRATION FAILED'); // TODO: handle registration failure
            }

            if (!data.eventPreferredTimes) {
                modalData.generalModalProps.startStep = CONFIRM_SUCCESS_STEP_NAME;
                setUserAsRegistered();
                return triggerFlowModal(eventRegistrationFlowType, modalData);
            }

            modalData.generalModalProps.eventPreferredTimes =
                data.eventPreferredTimes;
            modalData.generalModalProps.startStep = SELECT_TIME_STEP_NAME;
            modalData.generalModalProps.requiredSteps[
                SELECT_TIME_STEP_NAME
            ] = true;

            // event has preferred times - set step to time select and trigger modal
            return triggerFlowModal(eventRegistrationFlowType, modalData);
        }
    };

    handleEventRegisterClickMetrics = () => {
        const { event } = this.props;
        const sponsorIds = event.sponsors.map((company) => company.companyId);

        return EmitMetric({
            misc_event_count: 1,
            misc_event_type: 'event-register-click',
            event_id: event.id,
            company_id: sponsorIds,
        });
    };

    getUserEventStatus = () => {
        const { event, currentTime, isUserRegistered } = this.props;
        const {
            startTime,
            endTime,
            registrationDeadline,
            joinUrl,
            registrationDeactivated,
        } = event;

        const eventStatus = getEventStatus(startTime, endTime, currentTime);

        if (eventStatus === eventStatusTypes.EVENT_OVER) {
            return userEventStatusTypes.EVENT_OVER;
        }

        if (registrationDeactivated) {
            return userEventStatusTypes.DEACTIVE;
        }

        if (
            isEventRegistrationClosed(
                currentTime,
                startTime,
                registrationDeadline
            )
        ) {
            if (!isUserRegistered) {
                return userEventStatusTypes.REGISTRATION_CLOSED;
            }

            if (eventStatus === eventStatusTypes.IN_PROGRESS) {
                return userEventStatusTypes.REGISTERED_IN_PROGRESS;
            }

            return userEventStatusTypes.USER_REGISTERED_EVENT_OPEN;
        }

        if (isUserRegistered) {
            return registrationDeadline
                ? userEventStatusTypes.USER_REGISTERED
                : userEventStatusTypes.USER_REGISTERED_EVENT_OPEN;
        }

        return userEventStatusTypes.USER_NOT_REGISTERED;
    };

    checkShouldHideButton = ({
        event,
        sourceType,
        registrationUrl,
        userEventStatus,
    }) =>
        isEmpty(event) ||
        !registrationUrl ||
        userEventStatus === userEventStatusTypes.DEACTIVE ||
        (sourceType !== registrationSourceTypes.VFAIRS && !registrationUrl);

    render() {
        const { event, isSingleEventPage } = this.props;
        const { wrapAuth } = this.context;
        const { sourceType, registrationUrl, joinUrl } = event;

        const userEventStatus = this.getUserEventStatus();

        if (
            this.checkShouldHideButton({
                event,
                sourceType,
                registrationUrl,
                userEventStatus,
            })
        ) {
            return null;
        }

        let EventButton;

        switch (userEventStatus) {
            case userEventStatusTypes.REGISTERED_IN_PROGRESS:
                EventButton = (
                    <InProgressButton
                        isSingleEventPage={isSingleEventPage}
                        url={joinUrl}
                    />
                );
                break;
            case userEventStatusTypes.REGISTRATION_CLOSED:
                EventButton = <RegistrationClosedButton />;
                break;
            case userEventStatusTypes.EVENT_OVER:
                EventButton = <EventOverButton />;
                break;
            case userEventStatusTypes.USER_REGISTERED_EVENT_OPEN:
                EventButton = (
                    <AttendingButton
                        isSingleEventPage={isSingleEventPage}
                        url={joinUrl}
                    />
                );
                break;
            case userEventStatusTypes.USER_REGISTERED:
                EventButton = (
                    <AttendingButton isSingleEventPage={isSingleEventPage} />
                );
                break;
            default:
                EventButton = (
                    <RegisterButton onClick={this.triggerEventRegisterModal} />
                );
        }

        const buttonComponent = wrapAuth(
            'RegisterEvent',
            EventButton,
            registerButtonClickMetric
        );

        return (
            <div className="component-Events-EventRegisterButtonContainer">
                {buttonComponent}
            </div>
        );
    }
}
